import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetPublicSingleJob } from "../hooks/useGetSingleJob";
import { Skeleton } from "antd";
import logo from "assets/images/logoSvg.svg";
import {
  EnvironmentOutlined,
  CalendarOutlined,
  BankOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { routePath } from "config/routeMgt/routePath";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useMetaPixelForTrackedJobPage } from "../hooks/useMetaPixelForTrackedJob";

export const PublicJobDescription = () => {
  const { id } = useParams<{ id: string }>();
  useMetaPixelForTrackedJobPage(id);
  const { data: jobData, isLoading: jobLoading } = useGetPublicSingleJob(id);
  const { token } = useGetUserInfo();
  const navigate = useNavigate();
  const location = useLocation();

  const formattedDate = formatDate(jobData?.expiry_date);

  const isExpired = jobData?.expiry_date
    ? dayjs().isAfter(dayjs(jobData.expiry_date), "day")
    : false;

  const getIndustryName = () => {
    return jobData?.sector?.name || null;
  };

  const industryName = getIndustryName();

  const handleApplyClick = () => {
    if (token) {
      navigate(routePath.jobDetails(id).path);
    } else {
      navigate(routePath.sign_in, {
        state: {
          from: location,
          defaultAccountType: "talent",
        },
      });
    }
  };

  if (jobLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <header className="bg-white shadow-sm py-4 px-6 flex items-center gap-3 sticky top-0 z-50">
          <a
            href="https://www.arnopro.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={logo} alt="logo" className="h-8" />
          </a>
        </header>
        <main className="flex-grow p-6 md:p-10 max-w-4xl mx-auto w-full">
          <Skeleton active />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm py-4 px-6 flex items-center gap-3 sticky top-0 z-50">
        <a
          href="https://www.arnopro.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={logo} alt="logo" className="h-8" />
        </a>
      </header>

      {/* Main */}
      <main className="flex-grow p-6 md:p-10 max-w-4xl mx-auto w-full">
        <div className="bg-white p-8 rounded-2xl shadow-sm">
          <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-6">
            {jobData?.title || "Job Title"}
          </h1>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8 text-gray-600 flex-wrap">
            <div className="flex items-center gap-2">
              <BankOutlined className="text-blue-600" />
              <span className="font-medium">{jobData?.company_name}</span>
            </div>

            {industryName && (
              <div className="flex items-center gap-2">
                <TagsOutlined className="text-purple-600" />
                <span className="font-medium">{industryName}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <EnvironmentOutlined className="text-green-600" />
              <span>
                {jobData?.location_city}, {jobData?.location_country}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <CalendarOutlined className="text-red-600" />
              <span>
                Closing Date:{" "}
                <span
                  className={
                    isExpired ? "text-red-500 font-semibold" : "font-medium"
                  }
                >
                  {formattedDate}
                </span>
              </span>
            </div>
          </div>

          {/* Apply Button */}
          <div className="mt-10 flex justify-center">
            <button
              onClick={handleApplyClick}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg py-3 px-10 rounded-full shadow-md transition-all duration-200 hover:scale-105"
            >
              Apply for Role
            </button>
          </div>

          {/* Job Description Section */}
          <div className="border-t border-gray-100 pt-6 mt-6 text-gray-700 leading-relaxed">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">
              Job Description
            </h3>
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{
                __html: jobData?.description || "No description available.",
              }}
            />
          </div>

          {/* Competency/Skills Section */}
          {jobData?.skills && jobData.skills.length > 0 && (
            <div className="border-t border-gray-100 pt-6 mt-6">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                Required Skills & Competencies
              </h3>
              <div className="flex flex-wrap gap-3">
                {jobData.skills.map((skill: any, index: number) => {
                  const skillName =
                    typeof skill === "string" ? skill : skill.name;
                  return (
                    <span
                      key={typeof skill === "string" ? index : skill.id}
                      className="bg-purple-50 text-purple-700 text-sm font-medium px-4 py-2 rounded-full capitalize border border-purple-200"
                    >
                      {skillName}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
